import React, { useState, useEffect } from 'react';
import { AlertCircle, Send, MessageSquare, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent } from '@/components/ui/card';

const CaseMessages = ({ caseId }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [user, setUser] = useState(null);

  // Get current user
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  // Fetch messages
  const fetchMessages = async () => {
    try {
      setLoading(true);
      setError(null);

      const { default: api } = await import('../utils/api');
      const response = await api.get(`/api/cases/${caseId}/messages/`);

      if (response.data.status === 'success') {
        setMessages(response.data.messages || []);
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError('Failed to load messages. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (caseId) {
      fetchMessages();
    }
  }, [caseId]);

  // Send message
  const handleSendMessage = async () => {
    if (!newMessage.trim()) {
      toast.error('Message cannot be empty');
      return;
    }

    setSending(true);

    try {
      const { default: api } = await import('../utils/api');
      const response = await api.post(`/api/cases/${caseId}/messages/`, {
        message_text: newMessage,
      });

      if (response.data.status === 'success') {
        toast.success('Message sent successfully');
        setNewMessage('');
        fetchMessages();
      }
    } catch (err) {
      console.error('Error sending message:', err);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="p-4 border rounded-lg bg-white">
        <p className="text-sm text-slate-500">Loading messages...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>
          {error}
          <Button onClick={fetchMessages} size="sm" variant="outline" className="mt-2">
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {/* Messages list */}
      <div className="p-4 border rounded-lg bg-white">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-semibold">Messages ({messages.length})</span>
          <Button onClick={fetchMessages} size="sm" variant="ghost">
            <RefreshCw className="h-3.5 w-3.5 mr-1" />
            Refresh
          </Button>
        </div>

        {messages.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-8">
            <MessageSquare className="h-10 w-10 text-slate-300" strokeWidth={1.5} />
            <p className="text-sm text-slate-500">No messages yet</p>
            <p className="text-xs text-slate-400">Start the conversation below</p>
          </div>
        ) : (
          <ScrollArea className="h-[400px]">
            <div className="space-y-4 pr-4">
              {messages.map((msg) => {
                const isOwnMessage = user && msg.sender === user.id;

                return (
                  <Card
                    key={msg.id}
                    className={`${
                      isOwnMessage
                        ? 'ml-[20%] bg-blue-50/50'
                        : 'mr-[20%] bg-white'
                    }`}
                  >
                    <CardContent className="p-3 space-y-2">
                      {/* Header */}
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                          <Badge
                            variant={msg.sender_type === 'lawyer' ? 'info' : 'secondary'}
                            className="text-xs"
                          >
                            {msg.sender_type === 'lawyer' ? 'Lawyer' : 'Client'}
                          </Badge>
                          <span className="text-xs font-medium">
                            {msg.sender_name}
                          </span>
                        </div>
                        <span className="text-xs text-slate-500">
                          {formatDate(msg.created_at)}
                        </span>
                      </div>

                      {/* Message text */}
                      <p className="text-sm whitespace-pre-wrap">
                        {msg.message_text}
                      </p>

                      {/* Read status */}
                      {!msg.is_read && !isOwnMessage && (
                        <Badge variant="warning" className="text-xs">
                          Unread
                        </Badge>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </div>

      <Separator />

      {/* Send message form */}
      <div className="p-4 border rounded-lg bg-white">
        <div className="space-y-4">
          <span className="text-sm font-semibold">Send a message</span>

          <Textarea
            placeholder="Type your message here..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            rows={3}
            className="resize-none"
          />

          <div className="flex justify-end">
            <Button
              onClick={handleSendMessage}
              disabled={sending || !newMessage.trim()}
            >
              {sending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Send Message
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseMessages;
